/**
 * Button.tsx
 *
 * 216 files import this, which makes it the widest-reaching component in the app.
 *
 * ── Touch floor below `md` ──────────────────────────────────────────────────────
 * Controls get a 44px minimum hit area on narrow viewports. Desktop sizing is
 * deliberately untouched: raising the default height would visibly change density in
 * every toolbar, table row and form, which is a bigger change than the one being asked
 * for. So the floor is a `max-md:` minimum layered over the existing size map rather
 * than a new map.
 *
 * `max-md:` rather than a JS branch on purpose. Nothing leaves the flow, and a printed
 * page lays out near 816px so the escape does not fire during printing — printed
 * buttons keep their desktop size, which is what you want.
 *
 * ── Two exclusions, both unconditional ──────────────────────────────────────────
 * The `link` variant is styled as inline prose (`p-0 h-auto`); a 44px block inside a
 * sentence would break the line it sits in. The `xs` size is excluded at every
 * location because it is chosen precisely where space is tight.
 *
 * Neither exclusion can be made conditional on context: the component cannot see
 * whether it is inside a table cell, and the call sites that would need inspecting are
 * built by page-file column renderers this work does not touch. `lg` is already 44px,
 * so the floor is inert there.
 */
import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Stretch to full container width */
  fullWidth?: boolean;
  /** Renders as a square icon button */
  iconOnly?: boolean;
}

// ─── Style maps ───────────────────────────────────────────────────────────────

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground border border-transparent hover:bg-primary/90 active:bg-primary/80 focus-visible:ring-ring disabled:opacity-50',
  secondary:
    'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 focus-visible:ring-ring disabled:bg-slate-50 disabled:text-slate-400',
  danger:
    'bg-red-600 text-white border border-transparent hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500 disabled:bg-red-300',
  ghost:
    'bg-transparent text-slate-600 border border-transparent hover:bg-slate-100 active:bg-slate-200 focus-visible:ring-ring disabled:text-slate-400',
  link: 'bg-transparent text-primary border border-transparent hover:text-primary/80 hover:underline focus-visible:ring-ring disabled:text-slate-400 p-0 h-auto',
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'h-6 px-2 text-xs gap-1',
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-6 text-base gap-2',
};

const iconOnlySizeStyles: Record<ButtonSize, string> = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
};

/** Layered over the size map below `md`; never replaces it. */
const TOUCH_FLOOR = 'max-md:min-h-[44px]';
/** An icon button has no text to widen it, so it needs the floor on both axes. */
const TOUCH_FLOOR_ICON_ONLY = 'max-md:min-h-[44px] max-md:min-w-[44px]';

const spinnerSizeStyles: Record<ButtonSize, string> = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      iconOnly = false,
      className,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const touchFloor = variant !== 'link' && size !== 'xs';

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          // Base
          'inline-flex shrink-0 flex-nowrap items-center justify-center whitespace-nowrap font-medium rounded-md',
          'transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          'select-none cursor-pointer',
          'disabled:cursor-not-allowed',
          // Variant
          variantStyles[variant],
          // Size
          iconOnly ? iconOnlySizeStyles[size] : sizeStyles[size],
          // Touch floor below md, layered on top of the size above
          touchFloor && (iconOnly ? TOUCH_FLOOR_ICON_ONLY : TOUCH_FLOOR),
          // Full width
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {/* Loading spinner replaces leftIcon */}
        {loading ? (
          <Loader2 className={cn('animate-spin flex-shrink-0', spinnerSizeStyles[size])} />
        ) : leftIcon ? (
          <span className="inline-flex flex-shrink-0 items-center">{leftIcon}</span>
        ) : null}

        {!iconOnly && children && <span className="inline-flex items-center whitespace-nowrap">{children}</span>}

        {rightIcon && !loading && <span className="inline-flex flex-shrink-0 items-center">{rightIcon}</span>}
      </button>
    );
  },
);

Button.displayName = 'Button';
